import { useEffect, useState } from 'react';
import { PageHead, Err } from '../ui.js';
import { Link } from 'react-router-dom';
import { getSchedule, saveAutomation, ApiError } from '../api.js';
import type { ScheduleResponse } from '../api.js';

// ─────────────────────────────────────────────────────────────────────────────
// WHAT IS ABOUT TO HAPPEN.
//
// Today answers "what is queued". This answers "what will go out, to whom, on
// which day" — a different question, and the only one that lets somebody leave
// a sequence running without checking on it every morning. Automation without a
// forward view asks a person to trust software with their name on the From line
// and gives them nothing to look at, which is why most people switch it off
// after one surprise.
//
// THE SWITCH IS ON THIS SCREEN, NOT IN SETTINGS. Turning on unattended sending
// is a decision about the list below, so the list is what you should be looking
// at while you make it. A toggle buried three panels deep in Settings gets
// flipped by someone who has not seen what it will do.
//
// BLOCKED TOUCHES ARE SHOWN, NOT HIDDEN. The runner skips a touch with no copy,
// no address, a suppressed contact or an open conflict gate — all correctly,
// all silently. Hiding them here would make an empty Thursday look like a quiet
// week rather than four things needing ten minutes of work.
// ─────────────────────────────────────────────────────────────────────────────

const DAY = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

function dayLabel(iso: string): string {
  const today = new Date().toISOString().slice(0, 10);
  if (iso === today) return 'Today';
  const d = new Date(`${iso}T12:00:00Z`);
  const tomorrow = new Date(Date.now() + 86_400_000).toISOString().slice(0, 10);
  if (iso === tomorrow) return 'Tomorrow';
  return `${DAY[d.getUTCDay()]} ${d.getUTCDate()} ${d.toLocaleString('en-GB', { month: 'short', timeZone: 'UTC' })}`;
}

export function Schedule() {
  const [data, setData] = useState<ScheduleResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const load = () => getSchedule()
    .then(setData)
    .catch((e: unknown) => setError(e instanceof ApiError ? e.message : String(e)));

  useEffect(() => { void load(); }, []);

  async function patch(v: Record<string, unknown>) {
    setSaving(true); setError(null);
    try { await saveAutomation(v); await load(); }
    catch (e) { setError(e instanceof ApiError ? e.message : String(e)); }
    finally { setSaving(false); }
  }

  if (!data && error) {
    return (
      <div className="crashed">
        <strong>Couldn't load the schedule.</strong>
        <span>{error}</span>
        <button className="primary" onClick={() => { setError(null); void load(); }}>Retry</button>
      </div>
    );
  }
  if (!data) return <p className="loading">Loading…</p>;

  const s = data.settings;
  const on = Boolean(s.automation_enabled);
  const postal = String(s.postal_address ?? '').trim();

  return (
    <>
      <PageHead
        title="Schedule"
        count={data.sending}
        sub="What goes out, and when. Nothing here has been sent yet."
      />

      <Err>{error}</Err>

      {/* THE SWITCH, above the list it governs. */}
      <section className="panel">
        <h4>{on ? 'Sending on its own' : 'Waiting for you'}</h4>
        <p className="fieldhint">
          {on
            ? `Due touches are staged and sent between ${s.send_hour_start}:00 and `
              + `${s.send_hour_end}:00 ${s.send_weekdays_only ? 'on weekdays' : 'every day'}, `
              + `${s.send_timezone} time. Replies still stop an account immediately.`
            : 'Nothing sends unless you press it. Turn this on and the schedule below '
              + 'runs itself — inside your sending window, under your daily cap, '
              + 'and it still stops the moment somebody replies.'}
        </p>

        <div className="actions">
          <button className={on ? '' : 'primary'} disabled={saving}
            onClick={() => void patch({ automation_enabled: !on })}>
            {saving ? 'Saving…' : on ? 'Turn off unattended sending' : 'Let it send on its own'}
          </button>
        </div>

        {on && (
          <div className="pair">
            <label className="field">
              <span className="field-label">Start hour</span>
              <input type="number" min={0} max={23} defaultValue={Number(s.send_hour_start)}
                onBlur={(e) => void patch({
                  send_hour_start: Number(e.target.value),
                  // The pair rides together so the server's end-after-start
                  // check sees the whole window — patched singly, a bad
                  // window could be assembled one legal half at a time.
                  send_hour_end: Number(s.send_hour_end),
                })} />
            </label>
            <label className="field">
              <span className="field-label">Stop hour</span>
              <input type="number" min={1} max={24} defaultValue={Number(s.send_hour_end)}
                onBlur={(e) => void patch({
                  send_hour_start: Number(s.send_hour_start),
                  send_hour_end: Number(e.target.value),
                })} />
            </label>
          </div>
        )}
        {on && (
          <p className="fieldhint">
            Timezone <strong>{String(s.send_timezone)}</strong>.{' '}
            {s.automation_last_run_at
              ? `Last checked ${new Date(String(s.automation_last_run_at)).toLocaleString()}.`
              : 'Not run yet — the first pass happens on the hour.'}
          </p>
        )}
      </section>

      {/* THE ADDRESS THAT GOES ON COLD MAIL.
          Here rather than in Settings because this is the screen where sending
          is switched on, and the two facts belong to the same decision: no
          sequence arms without it, so burying it two screens away would mean
          discovering the requirement at the moment of being refused. The header
          carries its own answer — the design rule in CLAUDE.md — so a filled-in
          address needs no explanation and a missing one is impossible to miss. */}
      <section className="panel">
        <h4>
          {postal
            ? 'Sending address · on every cold message'
            : 'Sending address · required before any sequence arms'}
        </h4>
        <p className="fieldhint">
          The law that governs cold email wants two things in the message: a way
          out, and a real postal address for whoever sent it. The unsubscribe
          link is automatic. This is the address printed beside it. A registered
          agent or a PO box is fine — it has to be somewhere post would reach you.
        </p>
        <label className="field">
          <span className="field-label">Postal address</span>
          <input
            type="text"
            defaultValue={postal}
            placeholder="Company, street, city, state, postcode"
            onBlur={(e) => {
              if (e.target.value.trim() !== postal) {
                void patch({ postal_address: e.target.value });
              }
            }}
          />
        </label>
        {!postal && (
          <p className="fieldhint">
            Until this is filled in, arming a sequence is refused — a cold message
            without it is not a cadence with a gap, it is spam by definition.
          </p>
        )}
      </section>

      {data.days.length === 0 && (
        <p className="empty">
          Nothing scheduled. Arm a sequence on an account and its touches appear
          here on the days they are due.
        </p>
      )}

      {data.days.map((d) => (
        <section className="panel" key={d.date}>
          <h4>
            {dayLabel(d.date)}
            <span className="count">{d.sending} sending</span>
            {d.blocked > 0 && <span className="flag flag-warn">{d.blocked} blocked</span>}
          </h4>
          {d.touches.map((t) => (
            <div className="row" key={t.id}>
              <span className="rname">
                <Link to={`/companies/${t.company_slug}`}>{t.company}</Link>
                {t.contact ? ` · ${t.contact}` : ''}
              </span>
              <span className="meta">{t.subject}</span>
              {t.blocked
                ? <span className="flag flag-warn">{t.blocked}</span>
                : <span className="flag flag-ok">{t.channel}</span>}
            </div>
          ))}
        </section>
      ))}

      {/* "It never ran" and "it ran and there was nothing due" look identical
          from outside and have different fixes. */}
      {data.history.length > 0 && (
        <section className="panel">
          <h4>Recent passes</h4>
          {data.history.map((h, i) => (
            <div className="row" key={i}>
              <span className="rname">{new Date(String(h.ran_at)).toLocaleString()}</span>
              <span className="meta">
                {h.error ? `failed: ${String(h.error)}`
                  : h.skipped ? `skipped — ${String(h.skipped)}`
                    : `${h.staged} staged, ${h.paused} paused by replies`}
              </span>
            </div>
          ))}
        </section>
      )}
    </>
  );
}
